<template>
  <div class="audit-dashboard pa-4">
    <v-card flat class="border-light">
      <v-data-table
        :headers="loanHeaders"
        :items="auditLogs"
        :options.sync="options"
        :server-items-length="totalItems"
        :loading="loading"
        loading-text="Loading audit logs..."
        class="custom-audit-table"
        :footer-props="{
          'items-per-page-options': [10, 25, 50],
          'show-first-last-page': true,
          'items-per-page-text': 'Rows per page'
        }"
      >
        <!-- Module Column -->
        <template v-slot:[`item.module`]="{ item }">
          <div class="px-2">
            <v-chip x-small label class="font-weight-bold text-uppercase" color="grey lighten-3">
              {{ item.module }}
            </v-chip>
          </div>
        </template>

        <!-- Action Column -->
        <template v-slot:[`item.action`]="{ item }">
          <div class="d-flex align-center gap-2 px-2">
            <v-chip
              small
              label
              :color="getActionColor(item.action)"
              dark
              class="font-weight-bold text-uppercase"
            >
              {{ item.action }}
            </v-chip>
          </div>
        </template>

        <!-- User Column -->
        <template v-slot:[`item.whoChanged`]="{ item }">
          <div class="d-flex align-center">
            <v-avatar size="26" color="primary lighten-4" class="mr-2">
              <span class="primary--text text--darken-2 text-caption font-weight-black">
                {{ (item.whoChanged?.firstName || 'S')[0].toUpperCase() }}
              </span>
            </v-avatar>
            <span class="text-body-2 font-weight-medium">
              {{
                item.whoChanged
                  ? `${item.whoChanged.firstName} ${item.whoChanged.lastName || ''}`.trim()
                  : 'System'
              }}
            </span>
          </div>
        </template>

        <!-- Timestamp Column -->
        <template v-slot:[`item.createdAt`]="{ item }">
          <span class="text-body-2 grey--text text--darken-2">{{
            formatDate(item.createdAt)
          }}</span>
        </template>

        <!-- View Details Column -->
        <template v-slot:[`item.actions`]="{ item }">
          <v-btn
            x-small
            depressed
            color="primary lighten-5 primary--text"
            class="font-weight-bold"
            @click="showDetails(item)"
          >
            <v-icon x-small left>mdi-eye-outline</v-icon>
            View
          </v-btn>
        </template>
      </v-data-table>
    </v-card>

    <!-- Details Dialog -->
    <v-dialog v-model="detailsDialog" max-width="900px" scrollable>
      <v-card v-if="selectedLog" class="rounded-lg overflow-hidden">
        <!-- Header -->
        <div
          class="pa-4 border-b d-flex align-center justify-space-between"
          style="background: #f8f9fa"
        >
          <div class="d-flex align-center">
            <v-icon color="primary" class="mr-2">mdi-file-document-edit-outline</v-icon>
            <div>
              <div class="text-subtitle-1 font-weight-black grey--text text--darken-4">
                Audit Log Detail
              </div>
              <div class="text-caption grey--text">
                {{ selectedLog.module }} &bull; {{ formatDate(selectedLog.createdAt) }}
              </div>
            </div>
          </div>
          <v-btn icon small @click="detailsDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <v-card-text class="pa-4" style="max-height: 75vh; overflow-y: auto">
          <!-- Meta row -->
          <div
            class="d-flex flex-wrap gap-4 mb-5 pa-3 rounded"
            style="background: #f0f4ff; border: 1px solid #c7d4f7"
          >
            <div>
              <div class="text-caption grey--text font-weight-bold text-uppercase">Action</div>
              <v-chip
                small
                label
                dark
                :color="getActionColor(selectedLog.action)"
                class="font-weight-bold mt-1"
              >
                {{ selectedLog.action }}
              </v-chip>
            </div>
            <div class="border-l pl-4">
              <div class="text-caption grey--text font-weight-bold text-uppercase">Module</div>
              <div class="text-body-2 font-weight-black mt-1">{{ selectedLog.module }}</div>
            </div>
            <div class="border-l pl-4">
              <div class="text-caption grey--text font-weight-bold text-uppercase">
                Performed By
              </div>
              <div class="text-body-2 font-weight-black mt-1">
                {{
                  selectedLog.whoChanged
                    ? `${selectedLog.whoChanged.firstName} ${
                        selectedLog.whoChanged.lastName || ''
                      }`.trim()
                    : 'System'
                }}
              </div>
            </div>
            <div class="border-l pl-4">
              <div class="text-caption grey--text font-weight-bold text-uppercase">
                Date &amp; Time
              </div>
              <div class="text-body-2 font-weight-black mt-1">
                {{ formatDate(selectedLog.createdAt) }}
              </div>
            </div>
          </div>

          <!-- UPDATE: Before vs After comparison -->
          <template v-if="selectedLog.action === 'UPDATE'">
            <div class="text-subtitle-2 font-weight-black mb-3 d-flex align-center">
              <v-icon small class="mr-1" color="info">mdi-swap-horizontal</v-icon>
              Changes (Before → After)
            </div>
            <div
              v-if="parsedChanges.length === 0"
              class="text-center pa-6 grey lighten-5 rounded border"
            >
              <v-icon color="grey lighten-1" size="36">mdi-check-circle-outline</v-icon>
              <div class="text-body-2 grey--text mt-2">No field-level differences detected.</div>
            </div>
            <div v-else class="border rounded overflow-hidden">
              <table class="w-full change-table text-sm">
                <thead>
                  <tr class="grey lighten-4">
                    <th class="pa-3 text-left font-weight-bold border-r" style="width: 30%">
                      Field
                    </th>
                    <th class="pa-3 text-left font-weight-bold border-r" style="width: 35%">
                      <div class="d-flex align-center">
                        <v-icon x-small class="mr-1" color="red darken-1"
                          >mdi-arrow-left-circle</v-icon
                        >
                        Before
                      </div>
                    </th>
                    <th class="pa-3 text-left font-weight-bold" style="width: 35%">
                      <div class="d-flex align-center">
                        <v-icon x-small class="mr-1" color="green darken-1"
                          >mdi-arrow-right-circle</v-icon
                        >
                        After
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="change in parsedChanges" :key="change.key" class="border-t change-row">
                    <td class="pa-3 border-r">
                      <span
                        class="text-caption font-weight-black grey--text text--darken-3 text-uppercase"
                      >
                        {{ formatFieldName(change.key) }}
                      </span>
                    </td>
                    <td class="pa-2 border-r">
                      <div class="diff-box old">{{ formatDisplayValue(change.from) }}</div>
                    </td>
                    <td class="pa-2">
                      <div class="diff-box new">{{ formatDisplayValue(change.to) }}</div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </template>

          <!-- CREATE / DELETE: Snapshot view -->
          <template v-else>
            <div class="text-subtitle-2 font-weight-black mb-3 d-flex align-center">
              <v-icon
                small
                class="mr-1"
                :color="selectedLog.action === 'CREATE' ? 'success' : 'error'"
              >
                {{
                  selectedLog.action === 'CREATE'
                    ? 'mdi-plus-circle-outline'
                    : 'mdi-minus-circle-outline'
                }}
              </v-icon>
              {{
                selectedLog.action === 'CREATE'
                  ? 'Created Record Snapshot'
                  : 'Deleted Record Snapshot'
              }}
            </div>
            <div
              v-if="snapshotData.length === 0"
              class="text-center pa-6 grey lighten-5 rounded border"
            >
              <div class="text-body-2 grey--text">No snapshot data available.</div>
            </div>
            <v-row v-else dense class="border rounded ma-0">
              <v-col
                v-for="item in snapshotData"
                :key="item.key"
                cols="12"
                sm="6"
                md="4"
                class="pa-3 border-b border-r snapshot-cell"
              >
                <div class="text-caption grey--text font-weight-bold text-uppercase">
                  {{ formatFieldName(item.key) }}
                </div>
                <div class="text-body-2 font-weight-medium mt-1">
                  {{ formatDisplayValue(item.value) }}
                </div>
              </v-col>
            </v-row>
          </template>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { axiosUrl } from '@/utils/workforceApi'

const IGNORED_FIELDS = new Set([
  '_id',
  '__v',
  'updatedAt',
  'createdAt',
  'createdBy',
  'updatedBy',
  'loanDisbursementsData',
  'loanWorkflowData',
  'emiWorkflowData',
  'history'
])

export default {
  name: 'AuditTable',
  props: {
    caseConfig: Object
  },
  data() {
    return {
      loading: false,
      auditLogs: [],
      totalItems: 0,
      options: {
        page: 1,
        itemsPerPage: 10,
        sortBy: ['createdAt'],
        sortDesc: [true]
      },
      loanHeaders: [
        { text: 'Module', value: 'module', sortable: false },
        { text: 'Action', value: 'action', sortable: false },
        { text: 'Performed By', value: 'whoChanged', sortable: false },
        { text: 'Date & Time', value: 'createdAt', sortable: true },
        { text: '', value: 'actions', sortable: false, align: 'center' }
      ],
      // Details dialog
      detailsDialog: false,
      selectedLog: null
    }
  },
  computed: {
    parsedChanges() {
      const item = this.selectedLog
      if (!item || item.action !== 'UPDATE') return []

      const fromObj = item.changes?.before || {}
      const toObj = item.changes?.after || {}

      if (typeof fromObj !== 'object' || typeof toObj !== 'object') return []

      return Array.from(new Set([...Object.keys(fromObj), ...Object.keys(toObj)]))
        .filter((k) => !k.startsWith('_') && !IGNORED_FIELDS.has(k))
        .map((k) => ({ key: k, from: fromObj[k], to: toObj[k] }))
        .filter((c) => {
          const f = c.from == null ? '' : JSON.stringify(c.from)
          const t = c.to == null ? '' : JSON.stringify(c.to)
          return f !== t
        })
    },
    snapshotData() {
      const item = this.selectedLog
      if (!item) return []
      const snap = item.changes?.after || item.changes?.before || {}
      if (typeof snap !== 'object') return []
      return Object.keys(snap)
        .filter((k) => !IGNORED_FIELDS.has(k) && snap[k] != null)
        .map((k) => ({ key: k, value: snap[k] }))
    }
  },
  watch: {
    options: {
      handler() {
        this.fetchAudits()
      },
      deep: true
    },
    '$route.params.subCase': {
      handler() {
        this.options.page = 1
        this.fetchAudits()
      }
    }
  },
  mounted() {
    this.fetchAudits()
  },
  methods: {
    async fetchAudits() {
      this.loading = true
      try {
        const { page, itemsPerPage, sortBy, sortDesc } = this.options
        const subCaseId = this.$route.params.subCase

        const params = {
          page,
          limit: itemsPerPage,
          sortBy: sortBy?.[0] || 'createdAt',
          sortOrder: sortDesc?.[0] ? 'desc' : 'asc',
          subCase: subCaseId
        }

        const response = await this.$axios.get(axiosUrl.cases.getAuditLogs, { params })
        const responseData = response.data

        this.auditLogs = responseData?.data || []
        this.totalItems =
          responseData?.pagination?.totalCount ?? responseData?.total ?? this.auditLogs.length
      } catch (error) {
        console.error('Audit fetch error:', error)
      } finally {
        this.loading = false
      }
    },

    showDetails(item) {
      this.selectedLog = item
      this.detailsDialog = true
    },

    getActionColor(action) {
      return { CREATE: 'success', UPDATE: 'info', DELETE: 'error' }[action] || 'grey'
    },

    formatDate(date) {
      if (!date) return '-'
      return this.$dayjs(date).format('DD MMM YYYY, hh:mm A')
    },

    formatFieldName(field) {
      if (!field) return '-'
      return field
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, (s) => s.toUpperCase())
        .trim()
    },

    formatDisplayValue(val) {
      if (val === null || val === undefined) return '—'
      if (typeof val === 'boolean') return val ? 'Yes' : 'No'
      if (val instanceof Date || (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(val))) {
        return this.$dayjs(val).format('DD MMM YYYY, hh:mm A')
      }
      if (typeof val === 'object') {
        if (Array.isArray(val)) return `${val.length} item${val.length !== 1 ? 's' : ''}`
        return (
          val.name || val.fullName || val.firstName || val.label || JSON.stringify(val).slice(0, 60)
        )
      }
      return String(val)
    }
  }
}
</script>

<style scoped>
.custom-audit-table >>> th {
  background-color: #f5f5f5 !important;
  color: #555 !important;
  font-weight: 700 !important;
  font-size: 0.75rem !important;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.border-light {
  border: 1px solid #e0e0e0 !important;
}

.border {
  border: 1px solid #e0e0e0 !important;
}

.border-b {
  border-bottom: 1px solid #e0e0e0 !important;
}

.border-l {
  border-left: 1px solid #e0e0e0 !important;
}

.border-r {
  border-right: 1px solid #e0e0e0 !important;
}

.border-t {
  border-top: 1px solid #e0e0e0 !important;
}

/* Change comparison table */
.change-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;
}

.change-row:hover {
  background-color: #fafbff;
}

/* Diff boxes */
.diff-box {
  padding: 5px 10px;
  border-radius: 5px;
  font-size: 0.82rem;
  line-height: 1.5;
  word-break: break-word;
  min-height: 28px;
}

.diff-box.old {
  background-color: #fff1f0;
  color: #b91c1c;
  border: 1px solid #fecaca;
}

.diff-box.new {
  background-color: #f0fdf4;
  color: #15803d;
  border: 1px solid #bbf7d0;
  font-weight: 600;
}

/* Snapshot grid */
.snapshot-cell {
  border-bottom: 1px solid #eeeeee !important;
  border-right: 1px solid #eeeeee !important;
}

.gap-2 {
  gap: 8px;
}

.gap-4 {
  gap: 16px;
}

.w-full {
  width: 100%;
}

.text-sm {
  font-size: 0.875rem;
}
</style>
